// Unlock logic: licence-key storage, Worker validation, and an offline-safe
// cache so a paying customer is never stranded by a network blip.

import { WORKER_URL } from './config.js'

const KEY_LS = 'pe_license_key'
const CACHE_LS = 'pe_paid_places'

export function storedKey() {
  return localStorage.getItem(KEY_LS) || ''
}

export function isUnlocked() {
  return !!storedKey()
}

export function cachedPaid() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_LS) || 'null')
  } catch {
    return null
  }
}

export function clearUnlock() {
  localStorage.removeItem(KEY_LS)
  localStorage.removeItem(CACHE_LS)
}

/**
 * Validate a key against the Worker and, on success, persist the key + the
 * paid places. Throws { code } on failure so the UI can show the right message:
 *   'invalid'  — Worker rejected the key (bad/expired licence)
 *   'network'  — couldn't reach the Worker
 */
export async function unlockWithKey(txn) {
  let res
  try {
    res = await fetch(`${WORKER_URL}/paid-places?txn=${encodeURIComponent(txn)}`)
  } catch {
    throw { code: 'network' }
  }
  if (res.status === 403) throw { code: 'invalid' }
  if (!res.ok) throw { code: 'network', status: res.status }

  const data = await res.json()
  const places = data.places || []
  localStorage.setItem(KEY_LS, txn)
  localStorage.setItem(CACHE_LS, JSON.stringify(places))
  return places
}

// Restore on a new device: ask the Worker to email a one-time magic link.
// Always resolves (the Worker never reveals whether the email purchased).
export async function requestRestore(email) {
  try {
    await fetch(`${WORKER_URL}/restore`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
  } catch { /* optimistic — show the same confirmation regardless */ }
  return true
}

// Consume a magic link (?unlock=<token>) → cache paid data + store the buyer's
// txn so future loads re-validate normally. Throws { code } on failure.
export async function consumeMagic(token) {
  let res
  try {
    res = await fetch(`${WORKER_URL}/restore/consume?token=${encodeURIComponent(token)}`)
  } catch {
    throw { code: 'network' }
  }
  if (res.status === 403) throw { code: 'invalid' }
  if (!res.ok) throw { code: 'network', status: res.status }
  const data = await res.json()
  const places = data.places || []
  if (data.txn) localStorage.setItem(KEY_LS, data.txn)
  localStorage.setItem(CACHE_LS, JSON.stringify(places))
  return places
}

/**
 * Called on load when a key is already stored. Returns paid places immediately
 * from cache if present (so the map unlocks instantly and survives offline),
 * then refreshes from the Worker in the background. Returns null if not
 * previously unlocked.
 */
export async function restoreUnlock(onRefresh) {
  const key = storedKey()
  if (!key) return null
  const cached = cachedPaid()

  // Background refresh. A definitive 'invalid' (403) now means the buyer is
  // genuinely not entitled: every legit buyer — real, comp, or legacy — passes
  // either live Paddle validation OR the Worker's KV entitlement fallback, so a
  // 403 only happens for revoked/denylisted (chargeback/refund) or unknown
  // txns. So clear the unlock on 'invalid' to actually re-lock those. Transient
  // network errors are NOT 'invalid' → cached unlock is kept (never strand a
  // paying customer on a blip).
  unlockWithKey(key)
    .then((fresh) => onRefresh?.(fresh))
    .catch((err) => {
      if (err.code === 'invalid') { clearUnlock(); onRefresh?.(null, 'invalid') }
    })

  return cached
}
